import * as React from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { AppShell, OrgSwitcher, type AppShellNavItem, type AppShellNavSection } from '@redhand/ui';
import { THEME_NAMES, applyTheme, getStoredTheme, type ThemeName } from './theme.js';

const COMPONENT_PAGES: readonly { readonly id: string; readonly label: string; readonly href: string }[] = [
  { id: 'tokens', label: 'Tokeny a témata', href: '/tokens' },
  { id: 'app-shell', label: 'AppShell', href: '/components/app-shell' },
  { id: 'org-switcher', label: 'OrgSwitcher', href: '/components/org-switcher' },
  { id: 'layouts', label: 'Vstupní layouty', href: '/components/layouts' },
  { id: 'data-table', label: 'DataTable', href: '/components/data-table' },
  { id: 'kpi-card', label: 'KpiCard', href: '/components/kpi-card' },
  { id: 'charts', label: 'Grafy', href: '/components/charts' },
  { id: 'detail-timeline', label: 'DetailTimeline', href: '/components/detail-timeline' },
  { id: 'empty-state', label: 'EmptyState', href: '/components/empty-state' },
  { id: 'attention-list', label: 'AttentionList', href: '/components/attention-list' },
];

const SAMPLE_PAGES: readonly { readonly id: string; readonly label: string; readonly href: string }[] = [
  { id: 'dnesek', label: 'Dnešek (úkolový)', href: '/samples/dnesek' },
  { id: 'prehled', label: 'Přehled (číselný)', href: '/samples/prehled' },
  { id: 'seznam', label: 'Seznam + detail', href: '/samples/seznam' },
  { id: 'prazdny-start', label: 'Prázdný start', href: '/samples/prazdny-start' },
];

const THEME_LABELS: Readonly<Record<ThemeName, string>> = {
  reos: 'REOS (modrá)',
  kokpit: 'Kokpit (indigo)',
  'fakturace-a': 'Fakturace — kandidát A (emerald)',
  'fakturace-b': 'Fakturace — kandidát B (teal)',
};

function ThemeSwitcher(): React.JSX.Element {
  const [theme, setTheme] = React.useState<ThemeName>(() => getStoredTheme());
  return (
    <label className="gal-theme">
      <span className="gal-theme__label">Téma</span>
      <select
        className="rh-org__select"
        data-testid="theme-switcher"
        value={theme}
        onChange={(e) => {
          const next = e.target.value as ThemeName;
          setTheme(next);
          applyTheme(next);
        }}
      >
        {THEME_NAMES.map((name) => (
          <option key={name} value={name}>
            {THEME_LABELS[name]}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Galerie dogfooduje AppShell — jeji chrome je primo komponenta kitu. */
export function GalleryShell(): React.JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const withActive = (items: readonly { id: string; label: string; href: string }[]): readonly AppShellNavItem[] =>
    items.map((i) => ({ ...i, active: pathname === i.href }));
  const nav: readonly AppShellNavSection[] = [
    { id: 'components', label: 'Komponenty', items: withActive(COMPONENT_PAGES) },
    { id: 'samples', label: 'Vzorové obrazovky', items: withActive(SAMPLE_PAGES) },
  ];
  return (
    <AppShell
      productName="RedHand Kit"
      homeHref="/tokens"
      texts={{ skipLinkLabel: 'Přeskočit na obsah', navLabel: 'Hlavní menu', menuToggleLabel: 'Otevřít menu' }}
      nav={nav}
      renderNavItem={(item, className) => (
        <Link to={item.href} className={className} aria-current={item.active ? 'page' : undefined}>
          <span className="rh-shell__nav-label">{item.label}</span>
          {item.badge ? <span className="rh-badge rh-shell__nav-badge">{item.badge}</span> : null}
        </Link>
      )}
      orgSwitcher={
        <OrgSwitcher
          label="Aktivní organizace"
          orgName="RedHand — interní galerie"
          unitSelectLabel="Vybrat jednotku"
        />
      }
      notifications={{ label: 'Notifikace', count: 3 }}
      userSlot={<ThemeSwitcher />}
      footer="RedHand Kit v0 — interní galerie, vizuální regrese"
    >
      <Outlet />
    </AppShell>
  );
}
